import { useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { PageContainer } from '../../../../../components/page-container/page-container'
import { EyeView } from '../../../../../components/icons/eye-view'
import { Loading } from '../../../../../components/loading/loading'
import { Error } from '../../../../../components/error/error'
import { FilterModal } from '../../../../../components/filter-modal/filter-modal-new'

// Interface definida localmente para evitar problemas de cache
interface FilterOptions {
  status: string
  startDate: string
  endDate: string
}
import { paths } from '../../../../../router/paths'
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../hooks/redux.hooks'
import {
  fetchPreviousClosingsPaged,
  clearError,
} from '../../../../../store/journey/journey.slice'
import {
  formatCurrencyGTQ,
  formatTimeString,
} from '../../../../../utils/formatters'
import { getCurrentUserId } from '../../../../../utils/get-current-user'
import { styles, texts } from './previous-closings.styles'
import { PageSizeSelect } from '../../../../../components/page-size-select/page-size-select'
import { Button } from '../../../../../components/button'

export const PreviousClosings = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  // Ajuste: o slice de listagem paginada foi registrado como journeyList para não conflitar com JourneyReducer existente
  const { journeys, loading, error, pagination } = useAppSelector(
    (state: any) => state.journeyList,
  )

  // Estado local para filtros
  const [filters, setFilters] = useState<FilterOptions>({
    status: '-1', // inicial com todos os estados
    startDate: '',
    endDate: '',
  })
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)

  // Carregar dados ao montar o componente
  useEffect(() => {
    loadData()
  }, [currentPage, filters, pageSize])

  const loadData = () => {
    // Normalização de datas semelhante ao daily-sales
    let startDateToSend: string | undefined = filters.startDate || undefined
    let endDateToSend: string | undefined = filters.endDate || undefined

    if (startDateToSend && !endDateToSend) {
      endDateToSend = startDateToSend
    } else if (!startDateToSend && endDateToSend) {
      // Se somente endDate foi escolhido, usar endDate também como start para evitar range aberto no backend
      startDateToSend = endDateToSend
    }

    dispatch(
      fetchPreviousClosingsPaged({
        page: currentPage,
        pageSize,
        status:
          filters.status.toString() === '-1'
            ? undefined
            : Number(filters.status),
        startDate: startDateToSend,
        endDate: endDateToSend,
      }) as any,
    )
  }

  const handleGoBack = () => {
    navigate(paths.home)
  }

  const handleViewClosing = (closingId: string) => {
    const journey = journeys.find(
      (j: any) => String(j.journeyId) === String(closingId),
    )
    navigate(paths.closureDetail.replace(':id', closingId), {
      state: { journey },
    })
  }

  const handleRetry = () => {
    dispatch(clearError())
    loadData()
  }

  const handleApplyFilters = (newFilters: FilterOptions) => {
    setFilters(newFilters)
    setCurrentPage(1) // Reset para primeira página
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  // Função para formatar data
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('pt-BR')
    } catch {
      return dateString
    }
  }

  // Função para formatar hora
  const formatTime = (dateString: string) => formatTimeString(dateString)

  // Função para formatar valor monetário
  const formatCurrency = (amount: number) => formatCurrencyGTQ(amount)

  // Função para obter o texto do status
  const getStatusText = (status: string) => {
    switch (status) {
      case '1':
        return texts.statusClosed
      case '0':
        return texts.statusOpen
      default:
        return status
    }
  }

  // Função para obter a classe do status
  const getStatusClass = (status: string) => {
    switch (status) {
      case '1':
        return styles.statusClosed
      case '0':
        return styles.statusOpen
      default:
        return styles.statusClosed
    }
  }

  // Função para obter a classe do status do card
  const getCardStatusClass = (status: string) => {
    switch (status) {
      case '1':
        return styles.cardStatusClosed
      case '0':
        return styles.cardStatusOpen
      case '2':
        return styles.cardStatusCancelled
      case '3':
        return styles.cardStatusClosed // Usar closed como padrão para "Em Progresso"
      case '-1':
        return styles.cardStatusAll
      default:
        return styles.cardStatusClosed
    }
  }

  return (
    <PageContainer type="authenticated" title={texts.title}>
      <div className={styles.container}>
        {/* Header com informações de registros */}
        {!loading && !error && pagination && pagination.totalItems > 0 && (
          <div className={styles.headerContainer}>
            <h2 className={styles.headerTitle}>{texts.title}</h2>
            <div className={styles.headerInfo}>
              <div className={styles.recordsInfo}>
                {texts.showingRecords} {journeys?.length || 0} {texts.ofRecords}{' '}
                {pagination.totalItems} {texts.totalRecords}
              </div>
              <div className={styles.pageInfo}>
                {texts.page} {pagination.currentPage} {texts.ofPages}{' '}
                {pagination.totalPages}
              </div>
            </div>
          </div>
        )}

        {/* Filtros */}
        <div className={styles.filtersContainer}>
          <button
            className={styles.filterButton}
            onClick={() => setIsFilterModalOpen(true)}
          >
            <svg
              className="mr-2 h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
            {texts.filter}
          </button>
          <PageSizeSelect
            value={pageSize}
            onChange={val => {
              setPageSize(val)
              setCurrentPage(1)
            }}
            label={texts.items}
            options={[10, 20, 30, 50, 75, 100]}
          />
        </div>

        {/* Loading State */}
        {loading && <Loading text="Cargando..." />}

        {/* Error State */}
        {error && <Error message={error} onRetry={handleRetry} />}

        {/* Conteúdo Principal */}
        {!loading && !error && (
          <>
            {!journeys || journeys.length === 0 ? (
              // Estado vazio
              <div className={styles.emptyState}>
                <div className={styles.emptyStateIcon}>
                  <svg
                    className="h-16 w-16 text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <h3 className={styles.emptyStateTitle}>
                  {texts.emptyStateTitle}
                </h3>
                <p className={styles.emptyStateMessage}>
                  {texts.emptyStateMessage}
                </p>
                <div className={styles.emptyStateActions}>
                  <button
                    className={styles.emptyStateButton}
                    onClick={() => setIsFilterModalOpen(true)}
                  >
                    {texts.emptyStateFilterButton}
                  </button>
                  <button
                    className={styles.emptyStateButtonSecondary}
                    onClick={handleRetry}
                  >
                    {texts.emptyStateRefreshButton}
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Cards para Mobile */}
                <div className={styles.cardsContainer}>
                  {journeys.map((journey: any) => (
                    <div key={journey.journeyId} className={styles.card}>
                      <div className={styles.cardHeader}>
                        <div className={styles.cardDate}>
                          {formatDate(
                            journey.closingTime || journey.creationDate,
                          )}
                        </div>
                        <div className={styles.cardTime}>
                          {texts.journey.cardTime +
                            (journey.closingTime
                              ? formatTime(journey.closingTime)
                              : '-')}
                        </div>
                      </div>
                      <div className={styles.cardBody}>
                        <div className={styles.cardRow}>
                          <span className={styles.cardLabel}>
                            {texts.status}
                          </span>
                          <span
                            className={`${styles.cardStatus} ${getCardStatusClass(journey.status)}`}
                          >
                            {getStatusText(journey.status)}
                          </span>
                        </div>
                        <div className={styles.cardRow}>
                          <span className={styles.cardLabel}>
                            {texts.amount}
                          </span>
                          <span className={styles.cardAmount}>
                            {formatCurrency(journey.totalAmount)}
                          </span>
                        </div>
                        <div className={styles.cardActions}>
                          <button
                            className={styles.cardActionButton}
                            onClick={() =>
                              handleViewClosing(journey.journeyId.toString())
                            }
                            title="Ver detalles del cierre"
                          >
                            <EyeView width={20} height={20} color="#6B7280" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {/* Controles de Navegação - Sempre visíveis quando há dados */}
        {!loading &&
          !error &&
          journeys &&
          journeys.length > 0 &&
          pagination && (
            <div className={styles.paginationContainer}>
              {/* Informações de registros (só mostra se totalItems > 0) */}
              {pagination.totalItems > 0 && (
                <div className={styles.paginationInfo}>
                  {texts.showingRecords} {journeys.length} {texts.ofRecords}{' '}
                  {pagination.totalItems} {texts.totalRecords}
                </div>
              )}

              {/* Controles de navegação */}
              <div className={styles.paginationControls}>
                {/* Botão Primeira Página */}
                {pagination.totalPages > 1 && (
                  <button
                    className={styles.paginationButton}
                    onClick={() => handlePageChange(1)}
                    disabled={!pagination.hasPreviousPage}
                    title={texts.firstPage}
                  >
                    <svg
                      className={styles.paginationButtonIcon}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
                      />
                    </svg>
                  </button>
                )}

                {/* Botão Página Anterior */}
                <button
                  className={styles.paginationButton}
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={!pagination.hasPreviousPage}
                  title={texts.previousPage}
                >
                  <svg
                    className={styles.paginationButtonIcon}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>

                {/* Páginas numeradas (só se houver múltiplas páginas) */}
                {pagination.totalPages > 1 &&
                  Array.from(
                    { length: Math.min(5, pagination.totalPages) },
                    (_, i) => {
                      const pageNum =
                        Math.max(
                          1,
                          Math.min(
                            pagination.totalPages - 4,
                            pagination.currentPage - 2,
                          ),
                        ) + i
                      if (pageNum > pagination.totalPages) return null

                      return (
                        <button
                          key={pageNum}
                          className={
                            pageNum === pagination.currentPage
                              ? styles.paginationButtonActive
                              : styles.paginationButton
                          }
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </button>
                      )
                    },
                  )}

                {/* Botão Página Seguinte */}
                <button
                  className={styles.paginationButton}
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={!pagination.hasNextPage}
                  title={texts.nextPage}
                >
                  <svg
                    className={styles.paginationButtonIcon}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>

                {/* Botão Última Página */}
                {pagination.totalPages > 1 && (
                  <button
                    className={styles.paginationButton}
                    onClick={() => handlePageChange(pagination.totalPages)}
                    disabled={!pagination.hasNextPage}
                    title={texts.lastPage}
                  >
                    <svg
                      className={styles.paginationButtonIcon}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 5l7 7-7 7M5 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                )}
              </div>

              {/* Botão para carregar mais registros (sempre visível) */}
              <div className={styles.loadMoreContainer}>
                <button
                  className={styles.loadMoreButton}
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={!pagination.hasNextPage}
                >
                  <svg
                    className="mr-2 h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  {pagination.hasNextPage
                    ? texts.loadMore
                    : texts.noMoreRecords}
                </button>
              </div>
            </div>
          )}

        {/* Botão de Voltar */}
        <div className={styles.actionsContainer}>
          <Button className={styles.actionButton} onClick={handleGoBack}>
            {texts.goBack}
          </Button>
        </div>
        {/* Sem overlay adicional: mesmo comportamento da daily-sales */}
      </div>

      {/* Modal de Filtro */}
      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onApplyFilters={handleApplyFilters}
        currentFilters={filters}
        statusOptions={[
          { value: '-1', label: texts.statusAll },
          { value: '1', label: texts.statusClosed },
          { value: '0', label: texts.statusOpen },
        ]}
        defaultStatusValue="-1"
        statusLabel="Estado"
      />
    </PageContainer>
  )
}
